import { addNotification } from "portal/state/notifications";
import {
  anyContainingPermission,
  withAuthorizationRequired,
} from "portal/components/auth/WithAuthorizationRequired";
import { atkEquipmentApi } from "common/state/portalApi/atkEquipment";
import { Autotractor } from "protos/atk/autotractor";
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Skeleton,
  Typography,
} from "@mui/material";
import { ConfirmationDialog } from "portal/components/ConfirmationDialog";
import { EquipmentLibraryCard } from "portal/components/atk/EquipmentLibraryCard";
import {
  getNewRigPath,
  getRigDetailsPath,
  getRobotPath,
} from "portal/utils/routing";
import { getRigLibraryCardData } from "portal/components/atk/RigLibraryCardData";
import { GlobalHotKeys } from "react-hotkeys";
import { Header } from "portal/components/header/Header";
import { Page } from "portal/components/Page";
import {
  PermissionAction,
  PermissionDomain,
  PermissionResource,
} from "protos/portal/auth";
import { portalApi } from "portal/state/portalApi";
import { range } from "portal/utils/arrays";
import { ResponsiveSubnav } from "portal/components/ResponsiveSubnav";
import { RigPerformanceDetails } from "portal/views/autotractor/rig-details/RigPerformanceDetails";
import { RigStatus } from "portal/views/autotractor/rig-details/RigStatus";
import { RigValidationContext } from "../RigValidationContext";
import { RobotSelector } from "portal/components/robots/RobotSelector";
import { skipToken } from "@reduxjs/toolkit/query";
import { titleCase, valueOrLoading } from "portal/utils/strings";
import { UnauthorizedPage } from "portal/components/UnauthorizedPage";
import { useAtkRobotPageNav } from "portal/components/atk/AtkRobotSubnav";
import { useDispatch } from "react-redux";
import { useJigsawMetrics } from "../../../utils/hooks/useJigsawMetrics";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  useMutationPopups,
  useQueryPopups,
} from "portal/utils/hooks/useApiPopups";
import { useSelf } from "portal/state/store";
import { useTranslation } from "react-i18next";
import { WithSkeleton } from "portal/components/WithSkeleton";
import AddIcon from "@mui/icons-material/Add";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import React, { useState } from "react";

const _RigSwitcherPage: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { i18n, t } = useTranslation();
  const { measurementSystem } = useSelf();
  const dispatch = useDispatch();
  const useJigsaw = useJigsawMetrics();

  // ### ROBOT
  const { serial } = useParams();
  const { data: summary, isSuccess } = useQueryPopups(
    portalApi.endpoints.getRobot.useQuery(
      serial ? { language: i18n.language, serial, useJigsaw } : skipToken
    )
  );
  const { data: thisRobotRigs, isLoading: rigsLoading } = useQueryPopups(
    atkEquipmentApi.endpoints.searchRigs.useQuery({ serial, validate: true })
  );
  const [setActiveRig] = useMutationPopups(
    portalApi.endpoints.setActiveAtkRig.useMutation()
  );
  const [deleteRig] = useMutationPopups(
    atkEquipmentApi.endpoints.deleteRig.useMutation()
  );
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);

  const [subtitle, setSubtitle] = useState<string>("");

  let name: string = "";
  if (isSuccess) {
    name = valueOrLoading(summary.robot?.customerSerial, t);
  }

  const nav = useAtkRobotPageNav(undefined, summary);
  const [selectedRig, setSelectedRig] = useState<Autotractor | undefined>();

  const [detailsMenuAnchorEl, setDetailsMenuAnchorEl] = useState<
    Element | undefined
  >();
  const [detailsMenuRig, setDetailsMenuRig] = useState<
    Autotractor | undefined
  >();

  return (
    <>
      <GlobalHotKeys
        allowChanges
        keyMap={nav.keymap}
        handlers={nav.globalHotKeysHandlers}
      />
      <Header
        hideTitle={true}
        title={name}
        subtitle={subtitle}
        parentLink={getRobotPath(serial)}
      >
        <RobotSelector
          serial={serial}
          customerSerial={summary?.robot?.customerSerial}
          onChange={({ robot }) => {
            if (!serial || !robot) {
              return;
            }
            navigate(pathname.replace(serial, robot.serial));
          }}
          typography="h1"
          typographyClassName="text-lg"
        />
        {summary ? (
          <ResponsiveSubnav
            onChange={(index, name) => setSubtitle(name)}
            pages={nav.subnavPages}
          />
        ) : (
          <>
            {range(3).map((index) => (
              <Skeleton
                variant="rectangular"
                className="w-16 h-full"
                key={index}
              />
            ))}
          </>
        )}
      </Header>
      <Page>
        {confirmDelete && (
          <ConfirmationDialog
            description={t("components.ConfirmationDialog.delete.description", {
              subject: detailsMenuRig?.customerProvidedName,
            })}
            yesText={t("utils.actions.delete")}
            destructive={true}
            onClose={() => {
              setConfirmDelete(false);
            }}
            onYes={async () => {
              if (detailsMenuRig) {
                await deleteRig({ id: detailsMenuRig.id });
                setDetailsMenuAnchorEl(undefined);
                setDetailsMenuRig(undefined);
              } else {
                setConfirmDelete(false);
              }
            }}
          />
        )}
        <WithSkeleton
          variant="rectangular"
          className="min-h-10 w-full"
          success={true}
        >
          <RigValidationContext.Provider
            value={{
              rig: selectedRig,
              validationResult: selectedRig
                ? thisRobotRigs?.validationResults[selectedRig.id]
                : undefined,
              validationResultLoading: rigsLoading,
            }}
          >
            <div className="flex flex-row gap-8 w-full">
              <div className="flex flex-col gap-4 w-[40%] lg:w-[30%]">
                {thisRobotRigs &&
                  thisRobotRigs.rigs.map((rig, index) => (
                    <EquipmentLibraryCard
                      key={index}
                      data={getRigLibraryCardData(
                        t,
                        summary,
                        rig,
                        measurementSystem
                      )}
                      onCardClick={() => setSelectedRig(rig)}
                      variant="medium-no-image"
                      className={
                        selectedRig === rig
                          ? "border-2 border-solid border-carbon-orange"
                          : undefined
                      }
                      ActionButtonIcon={MoreHorizIcon}
                      onActionButtonClick={(e) => {
                        setDetailsMenuAnchorEl(e.currentTarget);
                        setDetailsMenuRig(rig);
                      }}
                    />
                  ))}
                <Menu
                  anchorEl={detailsMenuAnchorEl}
                  open={detailsMenuAnchorEl !== undefined}
                  onClose={() => setDetailsMenuAnchorEl(undefined)}
                >
                  <MenuItem
                    onClick={() => {
                      if (detailsMenuRig) {
                        navigate(getRigDetailsPath(detailsMenuRig.id));
                      }
                    }}
                  >
                    <ListItemIcon>
                      <EditIcon />
                    </ListItemIcon>
                    <ListItemText>
                      {titleCase(t("utils.actions.edit"))}
                    </ListItemText>
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      setConfirmDelete(true);
                    }}
                  >
                    <ListItemIcon>
                      <DeleteForeverIcon color="error" />
                    </ListItemIcon>
                    <ListItemText>
                      <Typography color="error">
                        {titleCase(t("utils.actions.delete"))}
                      </Typography>
                    </ListItemText>
                  </MenuItem>
                </Menu>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  className="rounded-xl text-lg"
                  onClick={() => navigate(getNewRigPath(serial ?? ""))}
                >
                  {t("utils.actions.newLong", {
                    subject: t("models.equipment.rig", { count: 1 }),
                  })}
                </Button>
              </div>
              <div className="flex flex-col gap-4 w-[60%] lg:w-[70%]">
                {selectedRig ? (
                  <>
                    <div className="flex flex-row justify-end gap-4">
                      <Button
                        variant="contained"
                        color="secondary"
                        className="rounded-lg"
                        onClick={() => setSelectedRig(undefined)}
                      >
                        {t("utils.actions.cancel")}
                      </Button>
                      <Button
                        disabled={
                          summary?.robot?.activeAtkRigId === selectedRig.id
                        }
                        variant="contained"
                        className="rounded-lg"
                        onClick={async () => {
                          if (serial) {
                            await setActiveRig({
                              serial,
                              rigId: selectedRig.id,
                            });
                            dispatch(
                              addNotification({
                                message: t(
                                  "views.autotractor.switchRig.activatedNotification",
                                  {
                                    rigName: selectedRig.customerProvidedName,
                                  }
                                ),
                                variant: "success",
                              })
                            );
                          }
                        }}
                      >
                        {t("utils.actions.activate")}
                      </Button>
                    </div>
                    <>
                      <RigStatus />
                      <RigPerformanceDetails />
                    </>
                  </>
                ) : (
                  <Typography color="secondary" className="text-center">
                    {t("views.autotractor.switchRig.detailsPlaceholder")}
                  </Typography>
                )}
              </div>
            </div>
          </RigValidationContext.Provider>
        </WithSkeleton>
      </Page>
    </>
  );
};

export const RigSwitcherPage = withAuthorizationRequired(
  // XXX: this should be changed to a different permission per SOFTWARE-5213.
  anyContainingPermission(
    PermissionAction.update,
    PermissionResource.atk_equipment,
    PermissionDomain.customer
  ),
  _RigSwitcherPage,
  <UnauthorizedPage />
);
